/**
 * 69.23's look (DECISIONS 237 (6), 239, 241 (4); the owner: "I think we should do a design sheet for mortar m113 as well"):
 * the mortar carrier's tube in the M113's cargo hatch, its crew, its rounds — the rig `design_sheets/mortar_m113.html` shows
 * and play switches on after the owner's picks. Lane C's step-4 look (vehicleLooks `m113D`, `MORTAR_LOOK`) is the start: a
 * medium tube on its baseplate in the cargo hatch, raised over the rear deck when set.
 *
 * The choices (each with a lean on the sheet):
 *   - `MortarTube`: A the medium tube (81 mm: lane C's size, the sim's "medium"), B the heavy (120 mm, as the late carriers).
 *   - `MortarMount`: A a turntable ring on the floor (the tube traverses on it), B a baseplate bolted to the floor on its own
 *     bipod (the carrier turns to lay it; the bipod gives a few degrees).
 *   - `MortarHatch`: A the one big lid on its rear hinge (today's M113), B the round hatch in three leaves — two side leaves
 *     folded out flat over the sides and a rear one back onto the deck: the roof wide open (the mortar carriers').
 *   - `MortarCrew`: A one (the sim's one crew seat: it lays, then loads — the round from the ready rack at the hatch's rim,
 *     raised to the muzzle and let go), B two (a gunner at the sight and a loader: a second seat, lane C's).
 *
 * In the hull's frame (vehicleLooks m113D: the roof at `MORTAR_RIG.roof`, the cargo hatch centred at `hatchZ`), person units
 * (a person is 1.8). Packed, the tube lies forward along the compartment under the shut hatch; setting (the sim's
 * `MORTAR.setupSeconds`), the hatch opens and the tube swings up over to its tilt toward the rear, the bipod's legs out to the
 * floor. Presentation only.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';

/**
 * The owner's picks (Oct 23): *"Let's take the lean for mortar design (tube a, turntable, hatch b, crew of 1)"* — the medium
 * tube, the turntable on its pedestal, the round hatch in three leaves, one crew. What play builds (vehicleLooks m113D).
 */
export const MORTAR_PICKS = { tube: 'A', mount: 'A', hatch: 'B', crew: 'A' } as const;

export type MortarTube = 'A' | 'B';
export type MortarMount = 'A' | 'B';
export type MortarHatch = 'A' | 'B';
export type MortarCrew = 'A' | 'B';
export const MORTAR_TUBES: Record<MortarTube, { length: number; bore: number; round: number; label: string }> = {
  A: { length: 1.4, bore: 0.065, round: 0.62, label: 'the medium tube (81 mm): lane C\'s size, the sim\'s "medium" mortar' },
  B: { length: 1.75, bore: 0.085, round: 0.8, label: 'the heavy tube (120 mm), as the late carriers carry: longer, fatter, a bigger blast' },
};
export const MORTAR_MOUNTS: Record<MortarMount, string> = {
  A: 'a turntable ring on the floor: the tube traverses on it (the carriers\' own mount)',
  B: 'a baseplate bolted to the floor on its own bipod: the carrier turns to lay it (a few degrees on the bipod)',
};
export const MORTAR_HATCHES: Record<MortarHatch, string> = {
  A: 'the one big lid on its rear hinge, leaning back over the rear (today\'s M113)',
  B: 'the round hatch in three leaves: two side leaves folded out flat over the sides, a rear one back onto the deck — the roof wide open, the mortar carriers\'',
};
export const MORTAR_CREWS: Record<MortarCrew, string> = {
  A: 'one: the sim\'s one crew seat — it lays at the sight, then loads each round from the ready rack at the hatch\'s rim',
  B: 'two: a gunner at the sight and a loader with the rounds (a second seat: lane C\'s)',
};

/**
 * The rig's measures in the hull's frame: the roof, the cargo hatch (its centre z, width, depth), the compartment's floor,
 * the mount (on a pedestal from the floor under the hatch's middle: the muzzle well over the roof, still in the crew's reach), the crew's feet (the
 * hatch's seat: left of the tube, the gunner's; on crew B the loader's, right of it), the tilts (set: from upright toward the
 * rear, ~64° up; packed: laid forward, level, under the shut hatch), the racks' slots (the store's most, MORTAR.storeRange's
 * 24), the ready rack's (a mission's three), the recoil (the barrel's slide and its seconds; the hull's buck is the host's).
 */
export const MORTAR_RIG = {
  roof: 2.0, hatchZ: -1.6, hatchW: 1.5, hatchD: 1.3, floor: 0.85,
  mount: { x: 0, y: 1.15, z: -1.45 },
  /**
   * the crew's feet along the hatch (they move round the tube: the sim's seat is one place, the drawing steps): laying, by the
   * sight (`lay` ahead of it: the right shoulder, facing the tube, just behind it); loading, by the muzzle (`load` ahead of it) — kept inside the hatch; on the heavy tube they stand on a step
   */
  follow: { lay: 0.25, load: 0.2 }, step: { A: 0, B: 0.45 } as Record<MortarTube, number>,
  crew: { x: -0.25, z: -1.75 }, loader: { x: 0.25, z: -1.75 },
  setTilt: 0.45, packedTilt: -(Math.PI / 2 - 0.06),
  racks: 24, ready: 3,
  recoil: { slide: 0.07, seconds: 0.35, buck: 0.012 },
};

export interface MortarRig {
  tubeKind: MortarTube; mountKind: MortarMount; hatchKind: MortarHatch; crewKind: MortarCrew;
  /** turns with the traverse (the turntable; on B, the bipod's few degrees) */
  mount: THREE.Group;
  /** pitches: rotation.x = −tilt (from upright toward the rear) */
  tube: THREE.Group;
  /** slides back along the tube with the recoil */
  barrel: THREE.Group;
  muzzle: THREE.Object3D;
  /** the gunner's hands (facing the tube from its left): the sight's knob toward the rear (the right hand), the traverse's handwheel toward the front (the left) */
  sightKnob: THREE.Object3D; handwheel: THREE.Object3D;
  /** the bipod's legs, each from the collar on the tube to its foot on the mount */
  legs: Array<{ bar: THREE.Mesh; collar: THREE.Object3D; foot: THREE.Vector3; folded: THREE.Vector3 }>;
  /** the hatch's leaves on their hinges: each turned `open` (radians about its axis) at full share */
  leaves: Array<{ hinge: THREE.Group; axis: 'x' | 'z'; open: number }>;
  /** the store: rounds standing in racks along the compartment's walls, one mesh, its draw range the count */
  racks: { mesh: THREE.Mesh; per: number };
  /** the ready rack at the hatch's rim (a mission's rounds), one mesh, its draw range the count; each slot's round middle */
  ready: { mesh: THREE.Mesh; per: number; slots: THREE.Object3D[] };
  /** the round in hand (the loader's), its hands' anchors */
  round: THREE.Group; roundHands: { right: THREE.Object3D; left: THREE.Object3D };
  /** the crew's feet: the gunner's (the hatch's seat) and, on crew B, the loader's */
  crew: THREE.Object3D; loader: THREE.Object3D;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color)); m.position.set(x, y, z); parent.add(m); return m;
}
function rod(parent: THREE.Object3D, r: number, len: number, color: string, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg = 10): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, seg), mat(color));
  if (axis === 'x') m.rotation.z = Math.PI / 2; else if (axis === 'z') m.rotation.x = Math.PI / 2;
  m.position.set(x, y, z); parent.add(m); return m;
}
const anchor = (parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D => { const o = new THREE.Object3D(); o.position.set(x, y, z); parent.add(o); return o; };
const group = (parent: THREE.Object3D, x = 0, y = 0, z = 0): THREE.Group => { const g = new THREE.Group(); g.position.set(x, y, z); parent.add(g); return g; };

/** A mortar round standing (its nose up): the body, the ogive, the fins' boom and its fins, the fuse — vertex colours, one geometry. */
function roundGeometry(len: number, r: number): THREE.BufferGeometry {
  const part = (g: THREE.BufferGeometry, colour: string): THREE.BufferGeometry => {
    const n = g.index ? g.toNonIndexed() : g, c = new THREE.Color(colour), cols = new Float32Array(n.getAttribute('position').count * 3);
    for (let i = 0; i < cols.length; i += 3) { cols[i] = c.r; cols[i + 1] = c.g; cols[i + 2] = c.b; }
    n.setAttribute('color', new THREE.BufferAttribute(cols, 3)); n.deleteAttribute('uv');
    return n;
  };
  const body = new THREE.CylinderGeometry(r, r * 0.8, len * 0.42, 8).translate(0, len * 0.45, 0);
  const ogive = new THREE.CylinderGeometry(r * 0.35, r, len * 0.22, 8).translate(0, len * 0.77, 0);
  const fuse = new THREE.CylinderGeometry(r * 0.2, r * 0.35, len * 0.08, 6).translate(0, len * 0.92, 0);
  const boom = new THREE.CylinderGeometry(r * 0.35, r * 0.35, len * 0.24, 6).translate(0, len * 0.12, 0);
  const fins = new THREE.BoxGeometry(r * 2, len * 0.14, 0.008).translate(0, len * 0.08, 0);
  const fins2 = new THREE.BoxGeometry(0.008, len * 0.14, r * 2).translate(0, len * 0.08, 0);
  return mergeGeometries([part(body, '#55603e'), part(ogive, '#55603e'), part(fuse, '#9a8a50'), part(boom, '#3c3f36'), part(fins, '#3c3f36'), part(fins2, '#3c3f36')], false)!;
}
/** 69.30: a mortar bomb as its own mesh (the tube's round: standing, its tail at y 0), for a crate's contents (rendering/ammoBoxes.ts). */
export function buildMortarBomb(tube: MortarTube = 'A'): THREE.Mesh {
  const T = MORTAR_TUBES[tube], m = new THREE.Mesh(roundGeometry(T.round, T.bore * 0.95), markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  return m;
}
/** Rounds standing at `spots` (in `parent`'s frame), one mesh, its draw range the count shown (`showRounds`). */
function roundRack(parent: THREE.Object3D, spots: THREE.Vector3[], len: number, r: number): { mesh: THREE.Mesh; per: number } {
  const one = roundGeometry(len, r), per = one.getAttribute('position').count;
  const mesh = new THREE.Mesh(mergeGeometries(spots.map(s => one.clone().translate(s.x, s.y, s.z)), false)!, markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  mesh.userData.noBake = true; parent.add(mesh);
  return { mesh, per };
}
export function showRounds(rack: { mesh: THREE.Mesh; per: number }, n: number, most: number): void {
  const k = Math.max(0, Math.min(most, Math.floor(n)));
  rack.mesh.geometry.setDrawRange(0, k * rack.per); rack.mesh.visible = k > 0;
}

/** The mortar's rig in the M113's hull (`hull`: m113D's), the picked look. The hull's own cargo lid must be gone (the hatch is the rig's). */
export function buildMortarRig(hull: THREE.Group, p: UnitPalette, o: { tube?: MortarTube; mount?: MortarMount; hatch?: MortarHatch; crew?: MortarCrew } = {}): MortarRig {
  const R = MORTAR_RIG, T = MORTAR_TUBES[o.tube ?? 'A'], mountKind = o.mount ?? 'A', hatchKind = o.hatch ?? 'B', crewKind = o.crew ?? 'A';
  const rig = group(hull); rig.name = 'mortar rig';
  // the mount: A the turntable ring with its handwheel; B a baseplate on the floor
  const mount = group(rig, R.mount.x, R.mount.y, R.mount.z); mount.name = 'mortar mount';
  const floor = -(R.mount.y - R.floor);   // (the compartment's floor under the mount)
  if (mountKind === 'A') {
    rod(mount, 0.42, 0.06, p.metal, 0, 0, 0, 'y', 16);
    rod(mount, 0.36, 0.03, p.dark, 0, 0.045, 0, 'y', 16);
    rod(mount, 0.09, -floor, p.metal, 0, floor / 2, 0, 'y', 8);   // the pedestal to the floor
  } else {
    box(mount, 0.5, 0.04, 0.5, p.metal, 0, 0, 0);
    for (const s of [-1, 1]) box(mount, 0.04, -floor, 0.04, p.metal, s * 0.2, floor / 2, s * 0.2);
  }
  // the tube: its base in a ball socket on the mount, the barrel (it slides with the recoil), the muzzle's ring, the sight
  const tube = group(mount, 0, 0.08, 0); tube.name = 'mortar tube';
  rod(tube, T.bore + 0.05, 0.08, p.metal, 0, 0, 0, 'y', 10);   // the socket
  const barrel = group(tube); barrel.name = 'barrel';
  rod(barrel, T.bore + 0.012, T.length, p.dark, 0, T.length / 2, 0, 'y', 12);
  rod(barrel, T.bore + 0.028, 0.06, p.metal, 0, T.length - 0.05, 0, 'y', 12);
  rod(barrel, T.bore + 0.03, 0.12, p.metal, 0, 0.1, 0, 'y', 12);   // the breech cap
  const muzzle = anchor(barrel, 0, T.length + 0.01, 0);
  // the sight on the tube's left (the gunner's side), the elevating gear's knob on it; the traverse's handwheel low in front of the collar
  const collarAt = T.length * 0.55;
  const collar = group(tube, 0, collarAt, 0);
  rod(collar, T.bore + 0.04, 0.09, p.metal, 0, 0, 0, 'y', 10);
  box(collar, 0.09, 0.13, 0.11, p.dark, -(T.bore + 0.1), 0.06, 0.02);   // the sight's box
  box(collar, 0.04, 0.05, 0.08, '#20282c', -(T.bore + 0.1), 0.15, 0.02);   // its eyepiece
  const sightKnob = anchor(collar, -(T.bore + 0.14), -0.02, -0.08);
  const wheelAt = group(collar, -(T.bore + 0.14), -0.16, 0.16);
  const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.012, 6, 12), mat(p.metal)); wheel.rotation.y = Math.PI / 2; wheelAt.add(wheel);
  rod(wheelAt, 0.012, 0.1, p.metal, 0.05, 0, 0, 'x', 6);   // its shaft to the collar
  const handwheel = anchor(wheelAt, -0.03, 0, 0.06);
  // the bipod's two legs: from the collar to feet on the mount (set), folded along the tube (packed); posed by poseMortar
  const legs: MortarRig['legs'] = [];
  for (const s of [-1, 1]) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.035, 1), mat(p.metal)); bar.userData.thin = true; mount.add(bar);
    legs.push({ bar, collar: anchor(collar, s * (T.bore + 0.04), 0, 0), foot: new THREE.Vector3(s * 0.34, 0.02, -0.38), folded: new THREE.Vector3(s * (T.bore + 0.05), 0.25, 0) });
  }
  // the hatch: A the one lid on its rear hinge; B three leaves (two sides folding out over the sides, one back onto the deck)
  const leaves: MortarRig['leaves'] = [], hz = R.hatchZ, hw = R.hatchW, hd = R.hatchD;
  if (hatchKind === 'A') {
    const h = group(rig, 0, R.roof + 0.05, hz - hd / 2 - 0.04);
    box(h, hw, 0.05, hd, p.body, 0, 0.012, hd / 2);
    leaves.push({ hinge: h, axis: 'x', open: -(Math.PI / 2 + 0.3) });
  } else {
    const rear = hd * 0.38, side = hd - rear;
    const hr = group(rig, 0, R.roof + 0.05, hz - hd / 2 - 0.04); box(hr, hw, 0.05, rear, p.body, 0, 0.012, rear / 2);
    leaves.push({ hinge: hr, axis: 'x', open: -(Math.PI - 0.12) });
    for (const s of [-1, 1]) {
      const hs = group(rig, s * (hw / 2 + 0.04), R.roof + 0.05, hz + rear / 2);
      box(hs, hw / 2, 0.05, side, p.body, -s * hw / 4, 0.012, 0);
      leaves.push({ hinge: hs, axis: 'z', open: s * (Math.PI - 0.12) });
    }
  }
  // the store: rounds standing in racks along the compartment's two walls, three rows of four a wall a tier, two tiers
  const spots: THREE.Vector3[] = [];
  for (let i = 0; i < R.racks; i++) {
    const wall = i % 2 === 0 ? 1 : -1, k = Math.floor(i / 2), tier = Math.floor(k / 6), col = k % 6;
    spots.push(new THREE.Vector3(wall * (1.22 - (tier * 0.16)), 0.9, -2.45 + col * 0.15 + tier * 0.07));
  }
  for (const wall of [-1, 1]) { box(rig, 0.34, 0.04, 1.0, p.metal, wall * 1.14, 0.88, -2.07); box(rig, 0.34, 0.04, 1.0, p.metal, wall * 1.14, 0.9 + T.round * 0.7, -2.07); }
  const racks = roundRack(rig, spots, T.round, T.bore * 0.95);
  // the ready rack at the hatch's rim by the loader's hand (a mission's three): the one crew's left, the loader's right
  const side = crewKind === 'A' ? -1 : 1;
  const readyAt = group(rig, side * 0.66, R.roof - 0.28 + (crewKind === 'B' ? R.step[o.tube ?? 'A'] : 0), R.crew.z + 0.3);   // (crew B's loader works from the step)
  box(readyAt, 0.3, 0.03, 0.12, p.metal, 0, 0, 0); box(readyAt, 0.3, 0.03, 0.12, p.metal, 0, T.round * 0.6, 0);
  const readySpots = [-0.1, 0, 0.1].map(x => new THREE.Vector3(x, 0.02, 0));
  const ready = { ...roundRack(readyAt, readySpots, T.round, T.bore * 0.95), slots: readySpots.map(s => anchor(readyAt, s.x, s.y + T.round / 2, s.z)) };
  // the round in hand, its hands' anchors (the body, the boom)
  const round = group(rig); round.name = 'round in hand'; round.visible = false;
  const rm = new THREE.Mesh(roundGeometry(T.round, T.bore * 0.95).translate(0, -T.round / 2, 0), markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }))); rm.userData.noBake = true; round.add(rm);
  const roundHands = { right: anchor(round, 0.04, 0.05, 0), left: anchor(round, -0.04, -0.2, 0) };
  // the crew face the tube: the gunner from its left (turned to +x), the loader (crew B) from its right
  const crew = anchor(hull, R.crew.x, R.roof - 1.15 + R.step[o.tube ?? 'A'], R.crew.z); crew.rotation.y = Math.PI / 2;
  const loader = anchor(hull, R.loader.x, R.roof - 1.15 + R.step[o.tube ?? 'A'], R.loader.z); loader.rotation.y = -Math.PI / 2;
  return { tubeKind: o.tube ?? 'A', mountKind, hatchKind, crewKind, mount, tube, barrel, muzzle, sightKnob, handwheel, legs, leaves, racks, ready, round, roundHands, crew, loader };
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _z = new THREE.Vector3(0, 0, 1);
const ease = (k: number) => { const c = Math.max(0, Math.min(1, k)); return c * c * (3 - 2 * c); };
/**
 * Pose the rig: `set` 0 packed … 1 set (the hatch opens through its first 0.35, the tube swings up through the rest, the legs
 * out to the floor last), `traverse` the bearing off the hull's rear (radians; the turntable's, or the bipod's few degrees on
 * mount B), `tilt` the elevation as a tilt from upright toward the rear (set; default the look's), `recoil` seconds since a
 * round left (absent: none), the store's `rounds` and the ready rack's `ready`.
 */
export function poseMortar(r: MortarRig, st: { set: number; traverse?: number; tilt?: number; recoil?: number; rounds: number; ready: number; /** crew A's task: 0 laying (by the sight) … 1 loading (by the muzzle); crew B's gunner lays, its loader loads */ load?: number }): void {
  const R = MORTAR_RIG, hatch = ease(st.set / 0.35), swing = ease((st.set - 0.35) / 0.5), legs = ease((st.set - 0.75) / 0.25);
  for (const l of r.leaves) { if (l.axis === 'x') l.hinge.rotation.x = l.open * hatch; else l.hinge.rotation.z = l.open * hatch; }
  r.mount.rotation.y = r.mountKind === 'A' ? (st.traverse ?? 0) : Math.max(-0.1, Math.min(0.1, st.traverse ?? 0));
  const tilt = st.tilt ?? R.setTilt;
  r.tube.rotation.x = -(R.packedTilt + (tilt - R.packedTilt) * swing);   // (−tilt leans it toward the rear; packed, toward the front)
  // the recoil: the barrel back into its socket and out again, the hull's buck is the host's (MORTAR_RIG.recoil.buck)
  const rk = st.recoil === undefined ? 1 : st.recoil / R.recoil.seconds;
  r.barrel.position.y = rk < 1 ? -R.recoil.slide * Math.sin(Math.PI * Math.min(1, rk)) * (1 - rk * 0.3) : 0;
  // the legs: from the collar to the foot (set) or folded along the tube
  r.mount.updateMatrixWorld(true);
  for (const l of r.legs) {
    const top = r.mount.worldToLocal(l.collar.getWorldPosition(_a));
    const foldAt = r.mount.worldToLocal(r.tube.localToWorld(_b.copy(l.folded)));
    const foot = foldAt.lerp(l.foot, legs);
    const d = foot.clone().sub(top), len = d.length();
    l.bar.position.copy(top).addScaledVector(d, 0.5);
    l.bar.quaternion.setFromUnitVectors(_z, d.normalize());
    l.bar.scale.set(1, 1, Math.max(0.05, len));
  }
  // the crew's feet along the hatch: by the sight to lay, by the muzzle to load (kept inside the hatch)
  const hull = r.crew.parent!;
  hull.updateMatrixWorld(true);
  // (across the hatch too: the gunner left of the tube, the loader right of it, as the turntable swings it)
  const inHatch = (z: number) => Math.max(R.hatchZ - R.hatchD / 2 + 0.08, Math.min(R.hatchZ + R.hatchD / 2 - 0.3, z));
  const acrossHatch = (x: number) => Math.max(-R.hatchW / 2 + 0.2, Math.min(R.hatchW / 2 - 0.2, x));
  const sight = hull.worldToLocal(r.sightKnob.getWorldPosition(_a)).clone(), muzzle = hull.worldToLocal(r.muzzle.getWorldPosition(_a)).clone();
  const tubeX = (z: number) => (Math.abs(muzzle.z - R.mount.z) > 1e-6 ? R.mount.x + (muzzle.x - R.mount.x) * (z - R.mount.z) / (muzzle.z - R.mount.z) : muzzle.x);   // (the tube's line over the floor)
  const layZ = inHatch(sight.z + R.follow.lay), loadZ = inHatch(muzzle.z + R.follow.load);
  const layX = acrossHatch(tubeX(sight.z) + R.crew.x), loadX = acrossHatch(muzzle.x + R.crew.x), loaderX = acrossHatch(muzzle.x + R.loader.x);
  const k = ease(st.load ?? 0), base = R.roof - 1.15, step = R.step[r.tubeKind];   // (the heavy tube's step: up onto it to load)
  const kc = r.crewKind === 'B' ? 0 : k;
  r.crew.position.set(layX + (loadX - layX) * kc, base + step * kc, layZ + (loadZ - layZ) * kc);
  r.loader.position.set(loaderX, base + step, loadZ);
  showRounds(r.racks, st.rounds - st.ready, R.racks);
  showRounds(r.ready, st.ready, R.ready);
}

// ---- in play: the crew's motions on a mission's rounds --------------------------------------------------------------------

/** A round's load, seconds before its fire: taken from the ready rack, raised, hung over the muzzle and let go; then the shot, the duck. */
export const MORTAR_LOAD = { take: 0.45, raise: 0.6, drop: 0.15, fire: 0.15, duck: 0.5 };
const LOAD_TOTAL = MORTAR_LOAD.take + MORTAR_LOAD.raise + MORTAR_LOAD.drop + MORTAR_LOAD.fire;
const _rq = new THREE.Quaternion(), _rs = new THREE.Vector3();
/** The round in hand at `k` of a load (0 at its ready-rack slot … 1 hung over the muzzle; past 1, dropping in), in the rig's frame. */
export function placeRound(r: MortarRig, slot: THREE.Object3D, k: number): void {
  const round = r.round, rig = round.parent!;
  rig.updateWorldMatrix(true, false);
  const from = rig.worldToLocal(slot.getWorldPosition(new THREE.Vector3()));
  r.muzzle.updateWorldMatrix(true, false);
  const m = new THREE.Vector3(); r.muzzle.matrixWorld.decompose(m, _rq, _rs);
  const axis = new THREE.Vector3(0, 1, 0).applyQuaternion(_rq), tubeQ = _rq.clone(), len = MORTAR_TUBES[r.tubeKind].round;
  const over = rig.worldToLocal(m.clone().addScaledVector(axis, len / 2 + 0.05)), drop = rig.worldToLocal(m.clone().addScaledVector(axis, -len / 2 - 0.1));
  const qTube = rig.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(tubeQ);
  if (k <= 1) {
    const e = ease(k);
    round.position.lerpVectors(from, over, e); round.position.y += 0.25 * Math.sin(Math.PI * e);
    round.quaternion.slerpQuaternions(new THREE.Quaternion(), qTube, e);
  } else { round.position.lerpVectors(over, drop, Math.min(1, k - 1)); round.quaternion.copy(qTube); }
}
/**
 * Pose the rig and its crew of one through a mission: `next` seconds to the next round's fire (absent: none coming), `last`
 * seconds since the last one left (absent: none), as `poseMortar`'s for the rest. The loader takes the round from the ready
 * rack (its top slot), raises it, hangs it and lets go as the next round's fire comes; the recoil and the duck follow the last.
 * Returns where the crew's hands go (the sight and the handwheel while set; the round's while loading; none packed) and how far
 * the crew ducks (0–1).
 */
export function poseMortarCrew(r: MortarRig, st: { set: number; traverse?: number; tilt?: number; rounds: number; ready: number; next?: number; last?: number }): { hands?: { right: THREE.Object3D; left: THREE.Object3D }; bend: number } {
  const L = MORTAR_LOAD, set = st.set >= 0.99;
  let load = 0, hand = false, roundK: number | null = null;
  if (set && st.next !== undefined && st.next <= LOAD_TOTAL && st.ready > 0) {
    const v = LOAD_TOTAL - st.next;
    load = v < L.take ? 0 : v < L.take + L.raise ? (v - L.take) / L.raise : 1;
    if (v < L.take + L.raise + L.drop) { roundK = v < L.take ? 0 : v < L.take + L.raise ? (v - L.take) / L.raise : 1 + (v - L.take - L.raise) / L.drop; hand = v < L.take + L.raise; }
  } else if (set && st.last !== undefined && st.last < L.duck + 0.4) load = st.last < L.duck ? 1 : 1 - (st.last - L.duck) / 0.4;
  const inHand = roundK !== null && roundK > 0 ? 1 : 0;
  poseMortar(r, { set: st.set, traverse: st.traverse, tilt: st.tilt, rounds: st.rounds, ready: Math.max(0, st.ready - inHand), load, ...(st.last !== undefined && st.last < MORTAR_RIG.recoil.seconds ? { recoil: st.last } : {}) });
  r.round.visible = roundK !== null;
  if (roundK !== null) placeRound(r, r.ready.slots[Math.max(0, Math.min(MORTAR_RIG.ready - 1, st.ready - 1))], roundK);
  const bend = set && st.last !== undefined && st.last < L.fire + L.duck ? Math.sin(Math.PI * st.last / (L.fire + L.duck)) : 0;
  return { hands: hand ? { right: r.roundHands.right, left: r.roundHands.left } : set ? { right: r.sightKnob, left: r.handwheel } : undefined, bend };
}

/**
 * A mission's lay in the hull's frame — `dir` the round's flight off the tube (its velocity, in the hull's frame: +z the front,
 * +y up) — as the rig's turntable bearing and tube tilt (the tube leans along the mount's −z).
 */
export function mortarLayOf(dir: { x: number; y: number; z: number }): { traverse: number; tilt: number } {
  const h = Math.hypot(dir.x, dir.z);
  return { traverse: Math.atan2(-dir.x, -dir.z), tilt: Math.PI / 2 - Math.atan2(dir.y, h) };
}

